import { Box, VStack, HStack } from '@terra/ui-styled-system';
import { Text } from '../../../../../components/styled/text';
import { Badge } from '../../../../../components/badge';
import { MarkdownPreview } from '../../../../markdown-preview';

type JwtRendererProps = {
  data: unknown;
  maxHeight?: string;
};

export function JwtRenderer({ data, maxHeight = '400px' }: JwtRendererProps) {
  let token: string | undefined;
  let header: Record<string, unknown> | undefined;
  let payload: Record<string, unknown> | undefined;
  let signature: string | undefined;

  if (typeof data === 'object' && data !== null) {
    const obj = data as Record<string, unknown>;

    token = typeof obj.token === 'string' ? obj.token : undefined;
    header =
      typeof obj.header === 'object' && obj.header !== null
        ? (obj.header as Record<string, unknown>)
        : undefined;
    payload =
      typeof obj.payload === 'object' && obj.payload !== null
        ? (obj.payload as Record<string, unknown>)
        : undefined;
    signature = typeof obj.signature === 'string' ? obj.signature : undefined;
  } else if (typeof data === 'string') {
    token = data;
  }

  return (
    <VStack alignItems="start" gap={2} width="100%">
      {/* Token */}
      {token && (
        <VStack alignItems="start" gap={1} width="100%">
          <Text fontSize="xs" fontWeight="medium" color="fg.default">
            {'Token:'}
          </Text>
          <Box
            bg="bg.canvas"
            p={2}
            borderRadius="sm"
            borderWidth="1px"
            borderColor="border.subtle"
            width="100%"
            maxHeight={maxHeight}
            overflow="auto"
          >
            <Text
              fontSize="xs"
              fontFamily="mono"
              color="fg.subtle"
              whiteSpace="pre-wrap"
              wordBreak="break-all"
            >
              {token}
            </Text>
          </Box>
        </VStack>
      )}

      {/* Header */}
      {header && (
        <VStack alignItems="start" gap={1} width="100%">
          <HStack gap={2} alignItems="center">
            <Text fontSize="xs" fontWeight="medium" color="fg.default">
              {'Header'}
            </Text>
            <Badge size="sm" variant="emphasis">
              {header.alg ? String(header.alg) : 'Unknown'}
            </Badge>
          </HStack>
          <Box
            bg="bg.canvas"
            p={2}
            borderRadius="sm"
            borderWidth="1px"
            borderColor="border.subtle"
            width="100%"
            maxHeight={maxHeight}
            overflow="auto"
          >
            <MarkdownPreview
              source={`\`\`\`json\n${JSON.stringify(header, null, 2)}\n\`\`\``}
            />
          </Box>
        </VStack>
      )}

      {/* Payload */}
      {payload && (
        <VStack alignItems="start" gap={1} width="100%">
          <Text fontSize="xs" fontWeight="medium" color="fg.default">
            {'Payload'}
          </Text>
          <Box
            bg="bg.canvas"
            p={2}
            borderRadius="sm"
            borderWidth="1px"
            borderColor="border.subtle"
            width="100%"
            maxHeight={maxHeight}
            overflow="auto"
          >
            <MarkdownPreview
              source={`\`\`\`json\n${JSON.stringify(payload, null, 2)}\n\`\`\``}
            />
          </Box>
        </VStack>
      )}

      {/* Signature */}
      {signature && (
        <VStack alignItems="start" gap={1} width="100%">
          <Text fontSize="xs" fontWeight="medium" color="fg.default">
            {'Signature'}
          </Text>
          <Box
            bg="bg.canvas"
            p={2}
            borderRadius="sm"
            borderWidth="1px"
            borderColor="border.subtle"
            width="100%"
            maxHeight={maxHeight}
            overflow="auto"
          >
            <Text
              fontSize="xs"
              fontFamily="mono"
              color="fg.subtle"
              whiteSpace="pre-wrap"
              wordBreak="break-all"
            >
              {signature}
            </Text>
          </Box>
        </VStack>
      )}
    </VStack>
  );
}
